import {
  TechDocsReaderPageContent,
  TechDocsReaderPageSubheader,
} from '@backstage/plugin-techdocs';
import { LightBox } from '@backstage/plugin-techdocs-module-addons-contrib';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { ReaderPageFrame } from './ReaderPageFrame';
import {
  TechDocsAddons,
  TechDocsReaderPageProvider,
} from '@backstage/plugin-techdocs-react';
import { CookieAuthRefreshProvider } from '@backstage/plugin-auth-react';
import { useEffect, useMemo, useRef } from 'react';
import clsx from 'clsx';
import { useLocation, useParams, useRoutes } from 'react-router-dom';
import { useSendPlatformxEvent } from '@internal/plugin-neo4j-platformx-common';

import {
  MkDocsLayoutStylesAddon,
  RecentPageHeaderAddon,
  RelatedLinksAddon,
} from '../plugin';
import { QueryClientProvider } from './QueryClientProvider';
import styles from './ReaderPage.module.css';

/**
 * The reader page for `/docs/:namespace/:kind/:name/*`.
 *
 * The reader finds TechDocs addons with `useOutlet()`. The app mounts each
 * page as a route without child routes, so the outlet is empty there. This
 * nested route puts the addons into the outlet, like `TechDocsReaderRouter`
 * of the Backstage TechDocs plugin, which the plugin does not export.
 */
export function ReaderPage() {
  return useRoutes([
    {
      path: '*',
      element: <ReaderPageProviders />,
      children: [
        {
          path: '*',
          // Must be the `TechDocsAddons` element itself, not a component that
          // renders it: the reader reads the addons from the element props.
          element: (
            <TechDocsAddons>
              <LightBox />
              <RelatedLinksAddon />
              <RecentPageHeaderAddon />
              <MkDocsLayoutStylesAddon />
            </TechDocsAddons>
          ),
        },
      ],
    },
  ]);
}

function ReaderPageProviders() {
  const { namespace = 'default', kind = 'component', name = '' } = useParams();
  const entityRef = useMemo(
    () => ({ kind, namespace, name }),
    [kind, namespace, name],
  );

  return (
    // CookieAuthRefreshProvider mirrors the Backstage TechDocsReaderPage: it fetches
    // the techdocs auth cookie the browser needs to load doc sub-resources
    // (CSS/JS/assets). Without it the reader hangs on the loading bar.
    <CookieAuthRefreshProvider pluginId="techdocs">
      <QueryClientProvider>
        <TechDocsReaderPageProvider entityRef={entityRef}>
          <ReaderLayout />
        </TechDocsReaderPageProvider>
      </QueryClientProvider>
    </CookieAuthRefreshProvider>
  );
}

function ReaderLayout() {
  const location = useLocation();
  const sendPlatformxEvent = useSendPlatformxEvent();
  const readerRef = useRef<HTMLDivElement>(null);

  // PluginPage scrolls inside its BUI FullPage element. The Backstage TechDocs
  // reader scrolls only the window to the top after a page change. Without this
  // effect, the new page keeps the scroll offset of the old page. With a
  // hash, the Backstage TechDocs reader scrolls to the heading.
  useEffect(() => {
    if (!location.hash) {
      readerRef.current?.closest('.bui-FullPage')?.scrollTo({ top: 0 });
    }
  }, [location.pathname, location.hash]);

  // Send who is reading platform updates
  useEffect(() => {
    const platformUpdatePath = location.pathname.match(
      /platform-updates-\d{4}-\d{2}/,
    )?.[0];

    if (platformUpdatePath) {
      sendPlatformxEvent?.(`aura-docs.${platformUpdatePath}.view`);
    }
  }, [location.pathname, sendPlatformxEvent]);

  return (
    <ReaderPageFrame>
      {/* The Backstage TechDocs reader clamps the fixed TOC to the top of the
          element with this class. The PluginPage header scrolls away with
          the content, so the TOC stops at the top of the window, and the
          class keeps it below the header if the frame changes later. */}
      <div
        ref={readerRef}
        className={clsx('techdocs-reader-page', styles.reader)}
      >
        <TechDocsReaderPageSubheader
          toolbarProps={{ className: styles.subheader }}
        />
        <Mermaid />
        {/* The search field of TechDocsReaderPageContent searches only the
            open site. The docs home has the search for every docs site. */}
        <TechDocsReaderPageContent withSearch={false} />
      </div>
    </ReaderPageFrame>
  );
}
